import { SearchForm } from "@/components/admin/search-form";
import { SectionHeader } from "@/components/dashboard/section-header";
import { InlineError } from "@/components/dashboard/state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/admin-api";
import type { Search, SearchCreate } from "@ow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";

const TIER_LABEL: Record<string, string> = { csuite: "C-Suite", vp_director: "VP/Dir", general: "General" };

export function AdminSearchesPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "searches"] });
  const { data: searches = [], isLoading } = useQuery({
    queryKey: ["admin", "searches"],
    queryFn: () => adminApi.searches.list(),
  });
  const [editing, setEditing] = useState<Search | null>(null);
  const [creating, setCreating] = useState(false);

  const createM = useMutation({
    mutationFn: (b: SearchCreate) => adminApi.searches.create(b),
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });
  const updateM = useMutation({
    mutationFn: ({ id, b }: { id: string; b: SearchCreate }) => adminApi.searches.update(id, b),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });
  const archiveM = useMutation({ mutationFn: (id: string) => adminApi.searches.archive(id), onSuccess: invalidate });

  return (
    <div className="space-y-5">
      <SectionHeader title="Searches">
        {!creating && !editing ? (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New search
          </Button>
        ) : null}
      </SectionHeader>

      <InlineError error={createM.error ?? updateM.error ?? archiveM.error} />

      {creating ? (
        <SearchForm
          onSubmit={(b) => createM.mutate(b)}
          submitting={createM.isPending}
          onCancel={() => setCreating(false)}
        />
      ) : null}
      {editing ? (
        <SearchForm
          initial={editing}
          onSubmit={(b) => updateM.mutate({ id: editing.id, b })}
          submitting={updateM.isPending}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr className="text-left">
              {["Role Title", "Tier", "Owner", "Status", ""].map((h) => (
                <th
                  key={h}
                  className="whitespace-nowrap px-3 py-2.5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : searches.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  No searches yet — create the first one.
                </td>
              </tr>
            ) : (
              searches.map((s) => (
                <tr key={s.id} className="border-b align-middle last:border-0 hover:bg-accent/50">
                  <td className="px-3 py-2.5 font-medium">
                    <Link to="/admin/searches/$searchId" params={{ searchId: s.id }} className="hover:underline">
                      {s.title}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">
                    {TIER_LABEL[s.tier] ?? s.tier}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{s.ownerName ?? "—"}</td>
                  <td className="px-3 py-2.5">
                    {s.launchedAt ? (
                      <StatusPill value={s.status} />
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-[var(--brand-accent)] bg-[var(--brand-accent)]/15 px-2 py-0.5 font-mono text-xs uppercase tracking-wide text-foreground">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                      <Button size="xs" variant="ghost" onClick={() => setEditing(s)}>
                        Edit
                      </Button>
                      {s.status !== "closed" ? (
                        <Button size="xs" variant="ghost" onClick={() => archiveM.mutate(s.id)}>
                          Archive
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
